<script setup lang="ts">
import { onMounted, onUnmounted } from "vue";

interface Props {
  open: boolean;
  title?: string;
  width?: string;
}

const props = withDefaults(defineProps<Props>(), { title: "", width: "560px" });
const emit = defineEmits<{ "update:open": [value: boolean] }>();

function close(): void {
  emit("update:open", false);
}

function onKey(e: KeyboardEvent): void {
  if (e.key === "Escape" && props.open) close();
}

onMounted(() => window.addEventListener("keydown", onKey));
onUnmounted(() => window.removeEventListener("keydown", onKey));
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="overlay" @click.self="close">
      <div class="modal" :style="{ width }">
        <header class="header">
          <h3 v-if="title">{{ title }}</h3>
          <button type="button" class="close" aria-label="Fermer" @click="close">×</button>
        </header>
        <div class="content">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.modal {
  background: var(--color-navy-light);
  border: 1px solid rgba(232, 160, 32, 0.25);
  border-radius: 12px;
  max-width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(232, 160, 32, 0.15);
}
.header h3 {
  font-family: var(--font-serif);
  font-size: 1.4rem;
  color: var(--color-cream);
  font-weight: 400;
}
.close {
  background: none;
  border: none;
  color: var(--color-gray);
  cursor: pointer;
  font-size: 1.6rem;
  width: 32px;
  height: 32px;
  border-radius: 6px;
}
.close:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--color-cream);
}
.content {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}
.footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(232, 160, 32, 0.15);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
</style>
